// Export the tour steps so they can be reused in page guides
export const createCampaignGuideSteps = [
  {
    element: "#CCF-campaign-name",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Campaign Name</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Enter a descriptive name for your Shopify discount campaign. This helps you identify it in your admin panel.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <p class="text-xs text-brand-800 font-medium">Example: "Summer Sale 20% Off" or "New Customer Welcome"</p>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-discount-code",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Discount Code</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Create a unique code that affiliates will share with their audience. This code will be applied at Shopify checkout.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <p class="text-xs text-brand-800 font-medium">Tip: Use uppercase letters and avoid spaces. Example: "SUMMER20" or "WELCOME10"</p>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-discount-value",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Discount Value</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Set the discount amount. Choose between percentage off or fixed dollar amount.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Percentage: 10% off (enter 10)</li>
              <li>• Fixed: $5 off (enter 5)</li>
              <li>• Maximum percentage: 100%</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-applies-to",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Applies To</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Choose which products the discount applies to in your Shopify store.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• All products: Discount applies to entire store</li>
              <li>• Specific products: Select individual products from your catalog</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-minimum-requirements",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Minimum Requirements</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Set purchase requirements before the discount can be applied.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• No minimum: Discount applies to any purchase</li>
              <li>• Minimum amount: Customer must spend $X</li>
              <li>• Minimum quantity: Customer must buy X items</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-maximum-discount-uses",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Usage Limits</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Control how many times this discount code can be used.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Unlimited: No limit on total uses</li>
              <li>• Limited: Set maximum total uses</li>
              <li>• One per customer: Each customer can use once</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-combinations",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
            <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3a4 4 0 118 0v4m-4 6v6m-4-6h8m-8 6h8"></path>
              </svg>
            </div>
            <span class="text-base font-semibold text-foreground">Combination Rules</span>
          </div>`,
      description: `<div class="space-y-3">
          <p class="text-sm text-muted-foreground">Choose if this discount can be combined with other Shopify discounts.</p>
          <div class="bg-brand-50 p-3 rounded-lg">
            <ul class="text-xs text-brand-800 space-y-1">
              <li>• Product discounts: Combine with product-specific discounts</li>
              <li>• Order discounts: Combine with order-level discounts</li>
              <li>• Shipping discounts: Combine with shipping promotions</li>
            </ul>
          </div>
        </div>`,
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "#CCF-save-button",
    popover: {
      title: `<div class="flex items-center gap-3 mb-3">
               <div class="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                 <svg class="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                 </svg>
               </div>
               <span class="text-base font-semibold text-foreground">Create Campaign</span>
             </div>`,
      description: `<div class="space-y-3">
                     <p class="text-sm text-muted-foreground">Save your campaign to create the discount code in Shopify. Affiliates can then start promoting it immediately.</p>
                     <div class="bg-brand-50 p-3 rounded-lg">
                       <p class="text-xs text-brand-800 font-medium">Your campaign will be active and ready for affiliates to promote!</p>
                     </div>
                   </div>`,
      side: "top" as const,
      align: "center" as const,
    },
  },
];


